import { Button, Card, Input, Label, Switch, useToast } from "randmarcomps";
import React, { useContext, useEffect, useState } from "react";
import { usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation } from "randmar-api-client";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import { Product } from "../../stores/product/Product";
import CountrySelector from "../util/CountrySelector";

interface Props {
  product: Product;
}

const EditDistributionInformationPanel: React.FC<Props> = ({ product }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const { toast } = useToast();
  const [updateProperty, { isLoading }] = usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation();

  const [upc, setUpc] = useState(product.upc || "");
  const [unitWeight, setUnitWeight] = useState(product.unitWeight || 0);
  const [masterCarton, setMasterCarton] = useState(product.masterCarton || 0);
  const [skidQuantity, setSkidQuantity] = useState(product.skidQuantity || 0);
  const [serialNumber, setSerialNumber] = useState(product.serialNumber || false);
  const [transparencyCode, setTransparencyCode] = useState(product.transparencyCode || false);
  const [opportunityOnly, setOpportunityOnly] = useState(product.opportunityOnly || false);
  const [countryCodeOfOrigin, setCountryCodeOfOrigin] = useState(product.countryCodeOfOrigin || "");

  useEffect(() => {
    setUpc(product.upc || "");
    setUnitWeight(product.unitWeight || 0);
    setMasterCarton(product.masterCarton || 0);
    setSkidQuantity(product.skidQuantity || 0);
    setSerialNumber(product.serialNumber || false);
    setTransparencyCode(product.transparencyCode || false);
    setOpportunityOnly(product.opportunityOnly || false);
    setCountryCodeOfOrigin(product.countryCodeOfOrigin || "");
  }, [product]);

  const saveChange = async (fieldName: string, newValue: string) => {
    try {
      await updateProperty({
        routeManufacturerId: applicationId,
        productNumber: product.randmarSKU || "",
        fieldName,
        newValue,
      }).unwrap();
      toast({ variant: "success", title: "Success", description: `Property ${fieldName} was successfully updated.` });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: `Property ${fieldName} failed to update.` });
    }
  };

  const handleSwitchChange = async (fieldName: string, checked: boolean) => {
    try {
      await updateProperty({
        routeManufacturerId: applicationId,
        productNumber: product.randmarSKU || "",
        fieldName,
        newValue: String(checked),
      }).unwrap();

      if (fieldName === "SerialNumber") setSerialNumber(checked);
      else if (fieldName === "TransparencyCode") setTransparencyCode(checked);
      else if (fieldName === "OpportunityOnly") setOpportunityOnly(checked);

      toast({ variant: "success", title: "Success", description: `Property ${fieldName} was successfully updated.` });
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: `Property ${fieldName} failed to update.` });
    }
  };
  
  const handleCountryChange = (newCountryCode: string) => {
    setCountryCodeOfOrigin(newCountryCode);
    saveChange("CountryCodeOfOrigin", newCountryCode.toUpperCase());
  };

  const showUpcUpdateButton = !(isNaN(+upc) || upc.length < 10 || upc.length > 14 || upc === product.upc || upc === "");
  const showUnitWeightUpdateButton = !(unitWeight === product.unitWeight || unitWeight === 0 || isNaN(unitWeight));
  const showMasterCartonUpdateButton = !(masterCarton < 0 || masterCarton === product.masterCarton || masterCarton === 0 || isNaN(masterCarton));
  const showSkidQuantityUpdateButton = !(skidQuantity < 0 || skidQuantity === product.skidQuantity || skidQuantity === 0 || isNaN(skidQuantity));

  return (
    <Card title="Warehousing Information" className="p-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="product-upc">UPC</Label>
          <div className="flex items-center gap-2">
            <Input
              id="product-upc"
              onChange={(e) => setUpc(e.target.value.toUpperCase())}
              placeholder="Edit UPC"
              value={upc}
              className="flex-1"
            />
            {showUpcUpdateButton && (
              <Button onClick={() => saveChange("UPC", upc)} disabled={isLoading}>
                Update
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-unitWeight">Unit weight (Lbs)</Label>
          <div className="flex items-center gap-2">
            <Input
              id="product-unitWeight"
              onChange={(e) => setUnitWeight(parseFloat(e.target.value))}
              value={unitWeight}
              placeholder="Edit unit weight"
              type="number"
              className="flex-1"
            />
            {showUnitWeightUpdateButton && (
              <Button onClick={() => saveChange("UnitWeight", unitWeight.toString())} disabled={isLoading}>
                Update
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-masterCarton">Master carton</Label>
          <div className="flex items-center gap-2">
            <Input
              id="product-masterCarton"
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val >= 0 || e.target.value === "") setMasterCarton(val);
              }}
              value={masterCarton}
              placeholder="Edit master carton"
              type="number"
              className="flex-1"
            />
            {showMasterCartonUpdateButton && (
              <Button onClick={() => saveChange("MasterCarton", masterCarton.toString())} disabled={isLoading}>
                Update
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="product-skid">Skid quantity</Label>
          <div className="flex items-center gap-2">
            <Input
              id="product-skid"
              onChange={(e) => {
                const val = parseInt(e.target.value);
                if (val >= 0 || e.target.value === "") setSkidQuantity(val);
              }}
              value={skidQuantity}
              placeholder="Edit skid quantity"
              type="number"
              className="flex-1"
            />
            {showSkidQuantityUpdateButton && (
              <Button onClick={() => saveChange("SkidQuantity", skidQuantity.toString())} disabled={isLoading}>
                Update
              </Button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id="serial-number"
            checked={serialNumber}
            onCheckedChange={(checked) => handleSwitchChange("SerialNumber", checked)}
            disabled={isLoading}
          />
          <Label htmlFor="serial-number" className="cursor-pointer">Record Serial Number</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="opportunity-only"
            checked={opportunityOnly}
            onCheckedChange={(checked) => handleSwitchChange("OpportunityOnly", checked)}
            disabled={isLoading}
          />
          <Label htmlFor="opportunity-only" className="cursor-pointer">Only available with Opportunity</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id="transparency-code"
            checked={transparencyCode}
            onCheckedChange={(checked) => handleSwitchChange("TransparencyCode", checked)}
            disabled={isLoading}
          />
          <Label htmlFor="transparency-code" className="cursor-pointer">Record Transparency Code</Label>
        </div>
        <div className="md:col-span-2">
          <CountrySelector value={countryCodeOfOrigin} onSelectionChange={handleCountryChange} />
        </div>
      </div>
    </Card>
  );
};

export default EditDistributionInformationPanel;
